import { Injectable, ErrorHandler, inject } from '@angular/core';
import { AuthService } from 'src/app/core/services/auth/auth.service';
import { Swal2 } from '../utils/swal2.util';
import { ConnectionService } from '../services/PWA/connection.service';
import { HttpErrorResponse } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { MatDialog } from '@angular/material/dialog';

@Injectable()
export class GlobalErrorHandler implements ErrorHandler {
  
    private supportMail = environment.mail_support;
    private auth = inject(AuthService);
    private swal = inject(Swal2);
    private connection = inject(ConnectionService);
    
    private dialog = inject(MatDialog)

    handleError(error: any): void {
        // console.log(error);
        if (this.connection.isConnected) {

            /** Error de Cliente */
            if (error instanceof ErrorEvent) {
                this.swal.showErrorAlert(error.error.message || 'Se produjo un error inesperado')
            }

            /** Error de Servidor */
            if (error instanceof HttpErrorResponse) {
                if (this.mayBeConnectionError(error)) {
                    this.diagnoseHttpError(error);
                } else {
                    this.isHttpErrorResponse(error)
                }
            }

            
        } else {
            this.connection.showToastError();
        }
    }

    private isHttpErrorResponse(error: HttpErrorResponse) {
        const codes: {[key:number]: () => void} = {
            401: () => { this.dialog.closeAll(); this.auth.logout(); },
            403: () => { this.dialog.closeAll(); this.auth.logout(); },
            500: () => this.swal.httpErrorAlert(500, this.getHttpHtml(error)),
            504: () => this.swal.httpErrorAlert(504, this.serverDownTemplate()),
            422: () => this.swal.httpErrorAlert(422, this.getHttpHtml(error)),
            404: () => this.swal.httpErrorAlert(404, this.getHttpHtml(error)),
            0: () => this.swal.httpErrorAlert(503, this.serverDownTemplate())
        };
        
        const codeKey = Object.keys(codes).includes(error.status.toString());

        if (codeKey) {
            codes[error.status]()
        } else {
            this.swal.httpErrorAlert(error.status || 500, this.getHttpHtml(error))
        }
    }

    /**
     * Sin respuesta (0) o 5xx: puede ser el servidor o la conexión del usuario.
     * Estando sin internet, el service worker responde 504 y el navegador da 0.
     */
    private mayBeConnectionError(error: HttpErrorResponse): boolean {
        return error.status === 0 || error.status >= 500;
    }

    /** Antes de culpar al servidor, comprueba si el backend y el internet responden */
    private async diagnoseHttpError(error: HttpErrorResponse) {
        const diagnosis = await this.connection.diagnose();
        if (diagnosis === 'noInternet') {
            this.swal.connectionErrorAlert(this.noInternetTemplate());
        } else if (diagnosis === 'serverDown') {
            this.swal.httpErrorAlert(503, this.serverDownTemplate());
        } else {
            this.isHttpErrorResponse(error);
        }
    }

    private noInternetTemplate(): string {
        return `
        <p>No pudimos comunicarnos con el servidor porque parece que no tienes conexión a internet.</p>
        <span>Revisa tu conexión e inténtalo nuevamente.</span>
        `;
    }

    private serverDownTemplate() {
        return `
        <p>El servidor no está respondiendo en este momento. Inténtalo nuevamente en unos minutos.</p>
        <div style="width: 100%;">
            Si el problema persiste, comunícate al correo: <strong>${this.supportMail}</strong> adjuntando una captura de pantalla del error
        </div></br>
        <span style="font-size: 14px;">Gracias por su comprensión y paciencia.</span>
        `
    }

    private getHttpMessage(error: HttpErrorResponse): string {
        return error?.error?.message || error.message || 'Ocurrió un error inesperado';
    }

    private getHttpHtml(error: HttpErrorResponse): string {
        const message = this.getHttpMessage(error);
        return message;
    }
}
